/*
 * Precios especiales por cliente (admin-only).
 *
 * Cada fila de la tabla superior es la lista de "specials" activos. El admin
 * puede pulsar "Nuevo" para agregar uno nuevo eligiendo cliente + ruta y luego
 * definir precios planos (precio_base, precio_kg, precio_cbm, precio_pallet,
 * precio_20ft/40ft/40hc) y/o escalones (brackets_kg, brackets_cbm,
 * brackets_pallet).
 *
 * Cuando este cliente pida una cotización, el backend fusionará estos
 * overrides sobre los precios de la ruta (`_apply_client_pricing`). Los
 * escalones respetan el patrón "De X a Y → precio" tal como el TierEditor
 * de Routes.jsx.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Plus, X, Trash2, TrendingUp, Star, Search } from "lucide-react";

const EMPTY = {
  client_id: "", route_id: "", activo: true,
  moneda: "",
  precio_base: 0, precio_kg: 0, precio_cbm: 0, precio_pallet: 0,
  precio_20ft: 0, precio_40ft: 0, precio_40hc: 0,
  brackets_kg: [], brackets_cbm: [], brackets_pallet: [],
  notas: "",
};

export default function ClientPricing() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [cp, cs, rs] = await Promise.all([
        api.get("/client-pricing"),
        api.get("/clientes"),
        api.get("/routes"),
      ]);
      setRows(cp.data || []);
      setClients(cs.data || []);
      setRoutes(rs.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clientById = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);
  const routeById = useMemo(() => Object.fromEntries(routes.map(r => [r.id, r])), [routes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const c = clientById[r.client_id];
      const ro = routeById[r.route_id];
      const hay = `${c?.fullname || ""} ${c?.email || ""} ${ro?.nombre || ""} ${ro?.origen || ""} ${ro?.destino || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, clientById, routeById, search]);

  const save = async () => {
    if (!form.client_id || !form.route_id) { toast.error("Cliente y ruta requeridos"); return; }
    try {
      await api.post("/client-pricing", form);
      toast.success(t("saved"));
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar precio especial?")) return;
    try { await api.delete(`/client-pricing/${id}`); load(); toast.success(t("deleted") || "Eliminado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (user.role !== "admin") return <div className="p-6 text-slate-500">Solo administradores</div>;

  return (
    <div className="space-y-4" data-testid="client-pricing">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            {t("client_special_pricing") || "Precios especiales por cliente"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t("client_special_pricing_hint") || "Estos precios se aplican automáticamente cuando el cliente cotiza o cuando alguien cotiza en su nombre."}
          </p>
        </div>
        <button
          data-testid="cp-new-btn"
          onClick={() => setForm({ ...EMPTY })}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> {t("new") || "Nuevo"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="cp-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search") || "Buscar cliente o ruta"}
            className="w-full ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-start text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="p-2 text-start">{t("client") || "Cliente"}</th>
                <th className="p-2 text-start">{t("route") || "Ruta"}</th>
                <th className="p-2 text-end">$/kg</th>
                <th className="p-2 text-end">$/CBM</th>
                <th className="p-2 text-end">$/pallet</th>
                <th className="p-2 text-center">{t("tiers") || "Escalones"}</th>
                <th className="p-2 text-center">Estado</th>
                <th className="p-2 text-end w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">{t("no_records") || "Sin registros"}</td></tr>
              )}
              {filtered.map(r => {
                const c = clientById[r.client_id];
                const ro = routeById[r.route_id];
                const numTiers = (r.brackets_kg?.length || 0) + (r.brackets_cbm?.length || 0) + (r.brackets_pallet?.length || 0);
                return (
                  <tr key={r.id} data-testid={`cp-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/70">
                    <td className="p-2">
                      <div className="font-semibold text-slate-900">{c?.fullname || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono">{c?.email || ""}</div>
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{ro?.nombre || "—"}</div>
                      <div className="text-xs text-slate-500">{ro?.origen} → {ro?.destino}</div>
                    </td>
                    <td className="p-2 text-end tabular-nums">{r.precio_kg || "—"}</td>
                    <td className="p-2 text-end tabular-nums">{r.precio_cbm || "—"}</td>
                    <td className="p-2 text-end tabular-nums">{r.precio_pallet || "—"}</td>
                    <td className="p-2 text-center">{numTiers > 0 ? <span className="px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-xs font-semibold">{numTiers}</span> : "—"}</td>
                    <td className="p-2 text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${r.activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                        {r.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="p-2 text-end">
                      <button
                        data-testid={`cp-edit-${r.id}`}
                        onClick={() => setForm({ ...EMPTY, ...r })}
                        className="text-xs font-semibold text-red-600 hover:underline me-3"
                      >
                        {t("edit") || "Editar"}
                      </button>
                      <button
                        data-testid={`cp-del-${r.id}`}
                        onClick={() => remove(r.id)}
                        className="text-xs text-slate-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <ClientPricingForm
          form={form}
          setForm={setForm}
          clients={clients}
          routes={routes}
          onClose={() => setForm(null)}
          onSave={save}
          t={t}
        />
      )}
    </div>
  );
}

function ClientPricingForm({ form, setForm, clients, routes, onClose, onSave, t }) {
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const route = routes.find(r => r.id === form.route_id);
  const mode = route?.modo || "";
  const showFcl = mode === "maritimo";

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cp-form">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            {form.id ? (t("edit") || "Editar") : (t("new") || "Nuevo")} — {t("client_special_pricing") || "Precio especial"}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t("client") || "Cliente"} *</label>
              <select
                data-testid="cp-client"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className={inp}
                disabled={!!form.id}
              >
                <option value="">—</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} ({c.email})</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t("route") || "Ruta"} *</label>
              <select
                data-testid="cp-route"
                value={form.route_id}
                onChange={(e) => setForm({ ...form, route_id: e.target.value })}
                className={inp}
                disabled={!!form.id}
              >
                <option value="">—</option>
                {routes.map(r => <option key={r.id} value={r.id}>{r.nombre} · {r.origen}→{r.destino} · {(r.modo || "").toUpperCase()}</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 space-y-3">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Precios planos (dejar 0 = usa el de la ruta)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><label className={lbl}>Base</label><input type="number" step="0.01" min="0" data-testid="cp-base" value={form.precio_base} onChange={(e)=>setForm({...form, precio_base: +e.target.value})} className={`${inp} bg-white`} /></div>
              <div><label className={lbl}>$/kg</label><input type="number" step="0.01" min="0" data-testid="cp-kg" value={form.precio_kg} onChange={(e)=>setForm({...form, precio_kg: +e.target.value})} className={`${inp} bg-white`} /></div>
              <div><label className={lbl}>$/CBM</label><input type="number" step="0.01" min="0" data-testid="cp-cbm" value={form.precio_cbm} onChange={(e)=>setForm({...form, precio_cbm: +e.target.value})} className={`${inp} bg-white`} /></div>
              <div><label className={lbl}>$/pallet</label><input type="number" step="0.01" min="0" data-testid="cp-pallet" value={form.precio_pallet} onChange={(e)=>setForm({...form, precio_pallet: +e.target.value})} className={`${inp} bg-white`} /></div>
            </div>
            {showFcl && (
              <div className="grid grid-cols-3 gap-3">
                <div><label className={lbl}>20'</label><input type="number" step="0.01" min="0" value={form.precio_20ft} onChange={(e)=>setForm({...form, precio_20ft: +e.target.value})} className={`${inp} bg-white`} /></div>
                <div><label className={lbl}>40'</label><input type="number" step="0.01" min="0" value={form.precio_40ft} onChange={(e)=>setForm({...form, precio_40ft: +e.target.value})} className={`${inp} bg-white`} /></div>
                <div><label className={lbl}>40 HC</label><input type="number" step="0.01" min="0" value={form.precio_40hc} onChange={(e)=>setForm({...form, precio_40hc: +e.target.value})} className={`${inp} bg-white`} /></div>
              </div>
            )}
          </div>

          <TierBlock label="Escalones $/kg" unit="kg" brackets={form.brackets_kg} setBrackets={(b)=>setForm({...form, brackets_kg: b})} tid="cp-kg-tier" />
          <TierBlock label="Escalones $/CBM" unit="CBM" brackets={form.brackets_cbm} setBrackets={(b)=>setForm({...form, brackets_cbm: b})} tid="cp-cbm-tier" />
          <TierBlock label="Escalones $/pallet" unit="pallet" brackets={form.brackets_pallet} setBrackets={(b)=>setForm({...form, brackets_pallet: b})} tid="cp-pallet-tier" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Moneda (opcional)</label>
              <select value={form.moneda} onChange={(e)=>setForm({...form, moneda: e.target.value})} className={inp}>
                <option value="">Usa la de la ruta</option>
                <option value="USD">USD</option><option value="EUR">EUR</option><option value="IQD">IQD</option><option value="SYP">SYP</option><option value="CNY">CNY</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.activo} onChange={(e)=>setForm({...form, activo: e.target.checked})} data-testid="cp-activo" />
                Activo
              </label>
            </div>
          </div>
          <div>
            <label className={lbl}>Notas internas</label>
            <textarea rows={2} value={form.notas} onChange={(e)=>setForm({...form, notas: e.target.value})} className={inp} />
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">{t("cancel") || "Cancelar"}</button>
          <button data-testid="cp-save" onClick={onSave} className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold">{t("save") || "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

function TierBlock({ label, unit, brackets, setBrackets, tid }) {
  const add = () => setBrackets([...(brackets || []), { desde: 0, precio: 0 }]);
  const update = (i, k, v) => {
    const n = [...brackets]; n[i] = { ...n[i], [k]: +v }; setBrackets(n);
  };
  const remove = (i) => setBrackets(brackets.filter((_, idx) => idx !== i));

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <button type="button" data-testid={`${tid}-add`} onClick={add} className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Añadir</button>
      </div>
      {(brackets && brackets.length > 0) ? (
        <div className="space-y-1.5">
          {[...brackets]
            .map((b, idx) => ({ ...b, __i: idx }))
            .sort((a, b) => (+a.desde || 0) - (+b.desde || 0))
            .map((b, si, arr) => {
              const hasta = si < arr.length - 1 ? +arr[si + 1].desde : null;
              const i = b.__i;
              return (
                <div key={`t-${i}`} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`${tid}-${i}`}>
                  <TrendingUp className="w-3 h-3 text-slate-400" />
                  <span className="text-slate-500">De</span>
                  <input type="number" step="0.01" min="0" value={b.desde} onChange={(e)=>update(i, "desde", e.target.value)} className="w-20 px-2 py-1 border border-slate-300 rounded-md" />
                  <span className="text-slate-500">a</span>
                  {hasta !== null ? (
                    <span className="w-20 px-2 py-1 border border-slate-200 rounded-md bg-slate-100 tabular-nums text-slate-700 text-center">{hasta}</span>
                  ) : (
                    <span className="w-20 px-2 py-1 border border-slate-200 rounded-md bg-slate-100 tabular-nums text-slate-500 text-center italic">∞</span>
                  )}
                  <span className="text-slate-500">{unit} →</span>
                  <span className="text-slate-500">$</span>
                  <input type="number" step="0.01" min="0" value={b.precio} onChange={(e)=>update(i, "precio", e.target.value)} className="w-24 px-2 py-1 border border-slate-300 rounded-md" />
                  <span className="text-slate-500">/{unit}</span>
                  <button type="button" onClick={()=>remove(i)} className="ms-auto p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
                </div>
              );
            })}
        </div>
      ) : (
        <p className="text-xs text-slate-400">Sin escalones (usa el precio plano de arriba)</p>
      )}
    </div>
  );
}
